import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { LimitWarning } from '../components/FeatureGate';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormatting';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SortableTableHead } from '../components/ui/sortable-table-head';
import { useSortableTable } from '../hooks/useSortableTable';
import useInfiniteList from '../hooks/useInfiniteList';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import {
  Plus,
  Pencil,
  Users,
  Search,
  Phone,
  Mail,
  Factory,
  MoreHorizontal,
  MapPin,
  Smartphone,
  Calendar,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MultiSelectFilter } from '../components/ui/multi-select-filter';
import { X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import OperatorEditDrawer from '../components/OperatorEditDrawer';
import WorkerAvailabilityTab from '../components/WorkerAvailabilityTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Operators = () => {
  const { api } = useAuth();
  const location = useLocation();
  const { checkLimit, refreshTierData, currentTier, hasFeature, hasTierFeature } = useTier();
  const [suppliers, setSuppliers] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [assetTypeFilters, setAssetTypeFilters] = useState([]);
  const [workerTypeFilters, setWorkerTypeFilters] = useState([]);
  const [activeFilter, setActiveFilter] = useState('active');
  const [archiveFilter, setArchiveFilter] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);

  // Auto-open Add Worker dialog when navigated with state.openDialog
  useEffect(() => {
    if (location.state?.openDialog) {
      setEditingOperator(null);
      setDialogOpen(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Cross-type email confirmation state
  const [crossTypeDialog, setCrossTypeDialog] = useState({ open: false, match: null, message: '', pendingData: null, pendingOperator: null });
  const [operatorToDelete, setOperatorToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
  const [complianceScores, setComplianceScores] = useState({});

  const fetchData = async () => {
    try {
      const [suppRes, eqTypesRes, wtRes, tenantRes] = await Promise.all([
        api.get('/suppliers?status=all'),
        api.get('/equipment-types'),
        api.get('/worker-types'),
        api.get('/tenants/current').catch(() => ({ data: {} })),
      ]);
      setSuppliers(suppRes.data);
      setEquipmentTypes((eqTypesRes.data || []).map(name => ({ name, is_active: true })));
      setWorkerTypes(wtRes.data);
      if (tenantRes.data?.date_format) setDateFormat(tenantRes.data.date_format);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const opList = useInfiniteList({
    endpoint: '/operators/paginated',
    search: searchTerm,
    params: {
      status: archiveFilter,
      is_active: activeFilter === 'all' ? '' : (activeFilter === 'active' ? 'true' : 'false'),
      supplier_id: supplierFilter === 'all' ? '' : supplierFilter,
      asset_type: assetTypeFilters.join(','),
      worker_type: workerTypeFilters.join(','),
    },
  });
  const operators = opList.items;
  const reloadOperators = () => opList.reload();

  useEffect(() => {
    fetchData();
  }, [api]);

  // Fetch compliance scores (Professional tier only)
  useEffect(() => {
    if (!hasTierFeature('ai_features')) return;
    api.get('/compliance/operator-scores')
      .then(res => {
        const map = {};
        (res.data || []).forEach(s => { map[s.operator_id] = s; });
        setComplianceScores(map);
      })
      .catch(() => {});
  }, [api, hasFeature, operators.length]);

  // Auto-select supplier for Basic tier with single supplier
  const defaultSupplierId = useMemo(() => {
    if (currentTier === 'basic' && suppliers.length === 1) {
      return suppliers[0].id;
    }
    return null;
  }, [currentTier, suppliers]);

  const handleOpenDialog = (op = null) => {
    setEditingOperator(op);
    setDialogOpen(true);
  };

  const handleQuickToggleMobile = async (op, enabled) => {
    try {
      await api.put(`/operators/${op.id}`, {
        ...op,
        mobile_enabled: enabled
      });
      toast.success(enabled ? 'Mobile access enabled — welcome email sent' : 'Mobile access disabled');
      reloadOperators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleResendMobileInvite = async (op) => {
    try {
      await api.post(`/operators/${op.id}/resend-mobile-invite`);
      toast.success(`Invite resent to ${op.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const handleSubmit = async (formData, operator, confirmCrossType = false) => {
    try {
      const payload = confirmCrossType ? { ...formData, confirm_cross_type: true } : formData;
      if (operator) {
        await api.put(`/operators/${operator.id}`, payload);
        toast.success('Worker updated');
      } else {
        await api.post('/operators', payload);
        toast.success('Worker added');
        refreshTierData();
      }
      setDialogOpen(false);
      setEditingOperator(null);
      setCrossTypeDialog({ open: false, match: null, message: '', pendingData: null, pendingOperator: null });
      reloadOperators();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.type === 'cross_type_warning') {
        setCrossTypeDialog({
          open: true,
          match: detail.match,
          message: detail.message,
          pendingData: formData,
          pendingOperator: operator,
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Operation failed');
      }
    }
  };

  const handleDelete = async (id) => {
    const operator = operators.find(o => o.id === id);
    setOperatorToDelete(operator);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!operatorToDelete) return;
    setIsDeleting(true);
    try {
      await api.delete(`/operators/${operatorToDelete.id}`);
      toast.success('Worker archived');
      reloadOperators();
      refreshTierData();
      setDeleteDialogOpen(false);
      setOperatorToDelete(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.reasons) {
        const r = detail.reasons;
        const parts = [];
        if (r.open_jobs) parts.push(`${r.open_jobs} open job${r.open_jobs > 1 ? 's' : ''}`);
        toast.error(`Cannot archive worker: ${parts.join(', ')}. Close these records first.`);
      } else {
        toast.error((typeof detail === 'string' && detail) || 'Archive failed');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestore = async (operator) => {
    try {
      await api.post(`/operators/${operator.id}/restore`);
      toast.success('Worker restored');
      reloadOperators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    }
  };

  const handleCreateSupplier = async (supplierData) => {
    try {
      const response = await api.post('/suppliers', supplierData);
      toast.success('Supplier created');
      const suppRes = await api.get('/suppliers');
      setSuppliers(suppRes.data);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create supplier');
      return null;
    }
  };

  // Server-side filtered + paginated via useInfiniteList; render items as-is.
  const filteredOperators = operators;

  const hasActiveFilters = supplierFilter !== 'all' || assetTypeFilters.length > 0 || workerTypeFilters.length > 0 || activeFilter !== 'active' || archiveFilter !== 'active';

  const clearAllFilters = () => {
    setSupplierFilter('all');
    setAssetTypeFilters([]);
    setWorkerTypeFilters([]);
    setActiveFilter('active');
    setArchiveFilter('active');
  };

  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier?.company_name || '-';
  };

  const getWorkerTypeNames = (workerTypeIds = []) => {
    return workerTypeIds
      .map(id => workerTypes.find(wt => wt.id === id)?.name)
      .filter(Boolean);
  };

  const { sortedData: sortedOperators, requestSort, getSortDirection } = useSortableTable(filteredOperators, {
    name: (op) => op.name?.toLowerCase(),
    code: (op) => (op.operator_code || '').toLowerCase(),
    supplier: (op) => (op.supplier_id ? getSupplierName(op.supplier_id) : 'zzz_internal').toLowerCase(),
    role: (op) => (op.role || '').toLowerCase(),
    location: (op) => [op.suburb, op.state].filter(Boolean).join(', ').toLowerCase(),
  });

  if (loading || (opList.loading && operators.length === 0)) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  // Check tier limit for adding new operators
  const limitCheck = checkLimit('operators', opList.total);

  return (
    <div className="space-y-6" data-testid="operators-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Workers</h1>
          <p className="text-muted-foreground">Operators, labourers, and supervisors</p>
        </div>
        <Button 
          className="btn-volt" 
          onClick={() => handleOpenDialog()} 
          data-testid="add-operator-btn"
          disabled={!limitCheck.allowed}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Worker
        </Button>
      </div>

      {/* Limit Warning */}
      {!limitCheck.allowed && (
        <LimitWarning 
          resource="operators"
          current={opList.total}
          limit={limitCheck.limit}
        />
      )}

      <Tabs defaultValue="workers" className="w-full">
        <TabsList>
          <TabsTrigger value="workers" data-testid="workers-tab">
            <Users className="h-4 w-4 mr-2" /> Workers
          </TabsTrigger>
          {hasFeature('worker_availability') && (
            <TabsTrigger value="availability" data-testid="availability-tab">
              <Calendar className="h-4 w-4 mr-2" /> Leave
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="workers" className="mt-4">

      {/* Shared Operator Edit Drawer */}
      <OperatorEditDrawer
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        operator={editingOperator}
        suppliers={suppliers}
        equipmentTypes={equipmentTypes}
        workerTypes={workerTypes}
        onSubmit={handleSubmit}
        onSupplierCreate={handleCreateSupplier}
        defaultSupplierId={defaultSupplierId}
      />

      <Card className="card-industrial">
        <CardHeader className="pb-3">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="operator-search"
                placeholder="Search workers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <Select value={archiveFilter} onValueChange={setArchiveFilter}>
                <SelectTrigger className="w-[140px]" data-testid="operator-archive-filter">
                  <SelectValue placeholder="Active" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              <Select value={activeFilter} onValueChange={setActiveFilter}>
                <SelectTrigger className="w-[140px]" data-testid="operator-active-filter">
                  <SelectValue placeholder="Active" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Activated</SelectItem>
                  <SelectItem value="inactive">Pending</SelectItem>
                </SelectContent>
              </Select>
              <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                <SelectTrigger className="w-[160px]" data-testid="operator-supplier-filter">
                  <SelectValue placeholder="All Suppliers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Suppliers</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <MultiSelectFilter
                label="All Asset Types"
                options={equipmentTypes.map(et => ({ value: et.name, label: et.name }))}
                selected={assetTypeFilters}
                onSelectionChange={setAssetTypeFilters}
                testId="operator-asset-type-filter"
                className="w-[170px]"
              />
              <MultiSelectFilter
                label="All Worker Types"
                options={workerTypes.map(wt => ({ value: wt.id, label: wt.name }))}
                selected={workerTypeFilters}
                onSelectionChange={setWorkerTypeFilters}
                testId="operator-worker-type-filter"
                className="w-[170px]"
              />
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="operator-clear-filters"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredOperators.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No workers found</p>
              <Button 
                className="mt-4 btn-volt" 
                onClick={() => handleOpenDialog()}
                disabled={!limitCheck.allowed}
              >
                Add Your First Worker
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead sortKey="name" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Name</SortableTableHead>
                    <SortableTableHead sortKey="code" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Code</SortableTableHead>
                    <SortableTableHead sortKey="supplier" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Supplier</SortableTableHead>
                    <SortableTableHead sortKey="role" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Role</SortableTableHead>
                    <TableHead className="label-overline">Contact</TableHead>
                    <SortableTableHead sortKey="location" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Location</SortableTableHead>
                    <TableHead className="label-overline">Assets</TableHead>
                    <TableHead className="label-overline">Worker Types</TableHead>
                    {hasTierFeature('ai_features') && <TableHead className="label-overline">Compliance</TableHead>}
                    <TableHead className="label-overline">Mobile</TableHead>
                    <TableHead className="label-overline text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedOperators.map((op) => (
                    <TableRow 
                      key={op.id} 
                      data-testid={`operator-row-${op.id}`}
                      className={`cursor-pointer hover:bg-muted/50 ${op.is_active === false ? 'opacity-60' : ''}`}
                      onClick={(e) => {
                        if (e.target.closest('button')) return;
                        handleOpenDialog(op);
                      }}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{op.name}</span>
                          {op.archived && (
                            <Badge variant="outline" className="text-xs text-muted-foreground border-muted-foreground/40 bg-muted/30" data-testid={`archived-badge-${op.id}`}>Archived</Badge>
                          )}
                          {op.is_active === false && (
                            <Badge variant="outline" className="text-xs text-red-400 border-red-400/40">Inactive</Badge>
                          )}
                          {!op.is_available && (
                            <Badge variant="secondary" className="text-xs">Unavailable</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {op.operator_code ? (
                          <Badge variant="outline" className="font-mono">{op.operator_code}</Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {op.supplier_id ? (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Factory className="h-3 w-3" />
                            {getSupplierName(op.supplier_id)}
                          </span>
                        ) : (
                          <Badge variant="outline" className="text-xs">Internal</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={op.role === 'supervisor' ? 'default' : 'secondary'}>
                          {op.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5 text-sm text-muted-foreground">
                          {op.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />{op.phone}
                            </div>
                          )}
                          {op.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />{op.email}
                            </div>
                          )}
                          {!op.phone && !op.email && '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {op.suburb || op.state ? (
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            {[op.suburb, op.state].filter(Boolean).join(', ')}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {op.equipment_types?.length > 1 ? (
                          <Badge variant="secondary" className="text-xs">{op.equipment_types.length} types</Badge>
                        ) : op.equipment_types?.length === 1 ? (
                          <Badge variant="secondary" className="text-xs">{op.equipment_types[0]}</Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {op.worker_type_ids?.length > 1 ? (
                          <Badge className="text-xs bg-violet-500/20 text-violet-600 dark:text-violet-400">{op.worker_type_ids.length} types</Badge>
                        ) : op.worker_type_ids?.length === 1 ? (
                          <Badge className="text-xs bg-violet-500/20 text-violet-600 dark:text-violet-400">{getWorkerTypeNames(op.worker_type_ids)[0]}</Badge>
                        ) : '-'}
                      </TableCell>
                      {hasTierFeature('ai_features') && (
                        <TableCell data-testid={`compliance-score-${op.id}`}>
                          {(() => {
                            const sc = complianceScores[op.id];
                            if (!sc) return <span className="text-muted-foreground text-xs">-</span>;
                            if (sc.total === 0) return <span className="text-muted-foreground text-xs">N/A</span>;
                            const color = sc.score >= 80 ? 'text-green-500 bg-green-500/10 border-green-500/30'
                              : sc.score >= 50 ? 'text-amber-500 bg-amber-500/10 border-amber-500/30'
                              : 'text-red-500 bg-red-500/10 border-red-500/30';
                            return (
                              <Badge variant="outline" className={`text-xs font-mono ${color}`} title={`${sc.valid} of ${sc.total} valid · ${sc.expired} expired · ${sc.expiring_soon} expiring soon`}>
                                {sc.score}%
                              </Badge>
                            );
                          })()}
                        </TableCell>
                      )}
                      <TableCell>
                        {op.mobile_enabled ? (
                          op.mobile_auth_status === 'pending' ? (
                            <Badge
                              className="bg-amber-500/20 text-amber-700 dark:text-amber-400 hover:bg-amber-500/30"
                              data-testid={`mobile-status-pending-${op.id}`}
                              title="Operator has been invited but has not set their password yet"
                            >
                              <Mail className="h-3 w-3 mr-1" />
                              Invite Pending
                            </Badge>
                          ) : (
                            <Badge
                              className="bg-green-500/20 text-green-600 hover:bg-green-500/30"
                              data-testid={`mobile-status-active-${op.id}`}
                            >
                              <Smartphone className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          )
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">
                            Disabled
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              data-testid={`operator-actions-${op.id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); handleOpenDialog(op); }}
                              data-testid={`edit-operator-${op.id}`}
                            >
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {op.mobile_enabled ? (
                              <>
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); handleResendMobileInvite(op); }}
                                  data-testid={`resend-mobile-invite-${op.id}`}
                                >
                                  <Mail className="h-4 w-4 mr-2" />
                                  Resend Invite
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleQuickToggleMobile(op, false); }}>
                                  <Smartphone className="h-4 w-4 mr-2" />
                                  Disable Mobile
                                </DropdownMenuItem>
                              </>
                            ) : (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleQuickToggleMobile(op, true); }}>
                                <Smartphone className="h-4 w-4 mr-2" />
                                Enable Mobile
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {op.archived ? (
                              <DropdownMenuItem
                                onClick={(e) => { e.stopPropagation(); handleRestore(op); }}
                                data-testid={`restore-operator-${op.id}`}
                              >
                                <ArchiveRestore className="h-4 w-4 mr-2" />
                                Restore
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={(e) => { e.stopPropagation(); handleDelete(op.id); }}
                                className="text-destructive focus:text-destructive"
                                data-testid={`archive-operator-${op.id}`}
                              >
                                <Archive className="h-4 w-4 mr-2" />
                                Archive
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div ref={opList.sentinelRef} />
          {filteredOperators.length > 0 && (
            <div className="flex items-center justify-between pt-4 text-sm text-muted-foreground" data-testid="operators-pagination-footer">
              <span>Showing {filteredOperators.length} of {opList.total}</span>
              {opList.hasMore && (
                <Button size="sm" variant="outline" onClick={opList.loadMore} data-testid="operators-load-more">Load more</Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Archive Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Archive Worker"
        description={`Archive "${operatorToDelete?.name}"? Archived workers are hidden from pickers and scheduling but all historical jobs, dockets, and timesheets are preserved. You can restore them later from the Archived filter.`}
        confirmText="Archive"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        testId="archive-operator-dialog"
      />

      {/* Cross-Type Email Confirmation Dialog */}
      <Dialog open={crossTypeDialog.open} onOpenChange={(open) => !open && setCrossTypeDialog(prev => ({ ...prev, open: false }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email Already In Use</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2">
                <p>{crossTypeDialog.message}</p>
                <p>Are you sure these are the <strong>same person</strong>? If confirmed, the email will be updated.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrossTypeDialog(prev => ({ ...prev, open: false }))}>
              Cancel
            </Button>
            <Button
              className="bg-amber-600 text-white hover:bg-amber-700"
              onClick={() => handleSubmit(crossTypeDialog.pendingData, crossTypeDialog.pendingOperator, true)}
              data-testid="confirm-cross-type-operator-btn"
            >
              Yes, Same Person
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        </TabsContent>

        {hasFeature('worker_availability') && (
          <TabsContent value="availability" className="mt-4">
            <WorkerAvailabilityTab api={api} operators={operators} formatDateDisplay={(d) => formatDate(d, dateFormat)} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Operators;
